/* HUENIT LAB 2.0 — Admin: 사용자 관리.
   관리 API(listUsers/setUserGroup/deleteUser) 실연동 — plan.md §5.
   등급 매핑: groups 빈=일반 · admin=선생님 · developer=개발자 (§5.3).
   등급 변경 후 대상 사용자는 재로그인해야 적용(§5.4) — 변경 시 안내 토스트. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Confirm, useToast, Loading, ErrorState } from '../../components/index.js';
import { useSession } from '../../auth/session.jsx';
import { listUsers, setUserGroup, deleteUser } from '../../api/adminApi.js';
import { fmtDate } from '../../lib/datetime.js';

const RELOGIN_NOTE = '권한이 변경되었습니다. 대상 사용자가 로그아웃 후 다시 로그인해야 적용됩니다.';

/* 등급 판정/표시 (§5.3) */
function tierOf(u) {
  const g = u.groups || [];
  if (g.includes('developer')) return 'developer';
  if (g.includes('admin')) return 'admin';
  return 'normal';
}
const TIERS = {
  normal:    { label: '일반',   style: {} },
  admin:     { label: '선생님', style: { background: 'var(--brand-50)', color: 'var(--brand-700)' } },
  developer: { label: '개발자', style: { background: '#eef5ff', color: 'var(--info)' } },
};
const FILTERS = [
  { value: 'all',       label: '전체' },
  { value: 'normal',    label: '일반' },
  { value: 'admin',     label: '선생님' },
  { value: 'developer', label: '개발자' },
];

function TierBadge({ tier }) {
  const t = TIERS[tier];
  return <span className="chip" style={t.style}>{t.label}</span>;
}

function StatusBadge({ user }) {
  if (!user.enabled) return <span className="chip" style={{ background: 'var(--warn-bg)', color: 'var(--warn)' }}><span className="dot" style={{ background: 'var(--warn)' }} />비활성</span>;
  if (user.status === 'CONFIRMED') return <span className="chip" style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}><span className="dot" style={{ background: 'var(--ok)' }} />정상</span>;
  return <span className="chip"><span className="dot" style={{ background: 'var(--ink-400)' }} />{user.status || '미인증'}</span>;
}

export function UsersAdmin() {
  const toast = useToast();
  const { getIdToken, email: myEmail, logout } = useSession();

  const [users, setUsers] = useState([]);
  const [nextToken, setNextToken] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);          // {message, status}
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);            // 변경 중인 username
  const [confirm, setConfirm] = useState(null);      // {title, body, confirmLabel, onConfirm}

  const fetchPage = useCallback(async (token) => {
    return listUsers(getIdToken, { limit: 60, paginationToken: token });
  }, [getIdToken]);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { users, paginationToken } = await fetchPage(undefined);
      setUsers(users);
      setNextToken(paginationToken);
    } catch (e) {
      setError({ message: e.message, status: e.status });
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => { reload(); }, [reload]);

  const loadMore = async () => {
    if (!nextToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const { users: more, paginationToken } = await fetchPage(nextToken);
      setUsers(prev => {
        const seen = new Set(prev.map(u => u.username));
        return [...prev, ...more.filter(u => !seen.has(u.username))];
      });
      setNextToken(paginationToken);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  /* groups 변경(선생님/개발자) — 성공 시 로컬 반영 + 재로그인 안내 */
  const changeGroup = async (user, group, action) => {
    setBusy(user.username);
    try {
      await setUserGroup(getIdToken, user.username, group, action);
      setUsers(prev => prev.map(u => {
        if (u.username !== user.username) return u;
        const set = new Set(u.groups || []);
        if (action === 'add') set.add(group); else set.delete(group);
        return { ...u, groups: [...set] };
      }));
      toast(RELOGIN_NOTE);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(null);
    }
  };

  const removeUser = async (user) => {
    setBusy(user.username);
    try {
      await deleteUser(getIdToken, user.username);
      setUsers(prev => prev.filter(u => u.username !== user.username));
      toast('사용자를 탈퇴 처리했습니다.');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(null);
    }
  };

  /* 확인 다이얼로그 트리거 */
  const askPromoteDeveloper = (user) => setConfirm({
    title: `${user.nickname || user.email} 님을 개발자로 지정할까요?`,
    body: '개발자(developer)는 전 사용자 관리·공식 예제 등록 권한을 가집니다. 신중히 부여하세요. 대상 사용자가 재로그인해야 적용됩니다.',
    confirmLabel: '개발자 지정',
    onConfirm: () => { setConfirm(null); changeGroup(user, 'developer', 'add'); },
  });
  const askDemoteDeveloper = (user) => setConfirm({
    title: `${user.nickname || user.email} 님의 개발자 권한을 해제할까요?`,
    body: '관리 콘솔 접근 권한이 사라집니다. 본인 계정을 해제하면 더 이상 이 화면을 쓸 수 없습니다.',
    confirmLabel: '개발자 해제',
    onConfirm: () => { setConfirm(null); changeGroup(user, 'developer', 'remove'); },
  });
  const askDelete = (user) => setConfirm({
    title: `${user.nickname || user.email} 님을 탈퇴 처리할까요?`,
    body: '되돌릴 수 없습니다. Cognito 계정과 본인 클라우드(S3) 데이터가 삭제됩니다. 공개·공식 게시물은 보존됩니다.',
    confirmLabel: '탈퇴 처리',
    onConfirm: () => { setConfirm(null); removeUser(user); },
  });

  /* 검색 + 등급 필터 (불러온 목록 대상 — 클라이언트 측) */
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter(u => {
      if (filter !== 'all' && tierOf(u) !== filter) return false;
      if (!q) return true;
      return (u.email || '').toLowerCase().includes(q) || (u.nickname || '').toLowerCase().includes(q);
    });
  }, [users, query, filter]);

  const counts = useMemo(() => {
    const c = { all: users.length, normal: 0, admin: 0, developer: 0 };
    users.forEach(u => { c[tierOf(u)] += 1; });
    return c;
  }, [users]);

  if (loading) {
    return <Loading title="사용자 목록을 불러오는 중…" />;
  }

  if (error) {
    return <ErrorState error={error} title="사용자 목록을 불러오지 못했습니다" onRetry={reload} onRelogin={logout} />;
  }

  return (
    <div>
      <div className="toolbar">
        <div className="search">
          <Icon name="user" size={17} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="이메일 또는 닉네임으로 검색" />
        </div>
        <div className="seg">
          {FILTERS.map(f => (
            <button key={f.value} type="button" className={"seg__opt" + (filter === f.value ? " is-on" : "")} onClick={() => setFilter(f.value)}>
              {f.label}<span style={{ opacity: .6, fontSize: 11 }}>{counts[f.value]}</span>
            </button>
          ))}
        </div>
        <button className="btn btn--ghost btn--sm" onClick={reload} title="새로고침"><Icon name="refresh" size={15} />새로고침</button>
      </div>

      <div className="panel">
        <div className="panel__head">
          <div>
            <div className="panel__title">사용자</div>
            <div className="panel__sub">{shown.length}명 표시 · 전체 {users.length}명{nextToken ? ' (더 있음)' : ''}</div>
          </div>
        </div>
        <div className="panel__body">
          {shown.length === 0 ? (
            <div className="empty-state" style={{ padding: '36px 20px' }}>
              <div className="empty-state__t">조건에 맞는 사용자가 없습니다</div>
              <div className="empty-state__d">검색어나 등급 필터를 바꿔 보세요.</div>
            </div>
          ) : (
            <div className="rows">
              {shown.map(u => {
                const tier = tierOf(u);
                const isSelf = !!myEmail && (u.email || '').toLowerCase() === myEmail.toLowerCase();
                const isBusy = busy === u.username;
                return (
                  <div className="row" key={u.username}>
                    <div className="row__main">
                      <div className="row__title">
                        {u.nickname || <span style={{ color: 'var(--ink-400)' }}>(닉네임 없음)</span>}
                        <TierBadge tier={tier} />
                        {isSelf && <span className="chip" style={{ background: 'var(--ink-100)', color: 'var(--ink-500)' }}>나</span>}
                      </div>
                      <div className="row__sub" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span className="mono">{u.email}</span>
                        <StatusBadge user={u} />
                        <span>가입 {fmtDate(u.created_at)}</span>
                      </div>
                    </div>
                    <div className="row__actions">
                      {tier === 'admin'
                        ? <button className="btn btn--ghost btn--sm" disabled={isBusy} onClick={() => changeGroup(u, 'admin', 'remove')}>선생님 강등</button>
                        : tier === 'normal'
                          ? <button className="btn btn--soft btn--sm" disabled={isBusy} onClick={() => changeGroup(u, 'admin', 'add')}>선생님 승격</button>
                          : null}
                      {tier === 'developer'
                        ? <button className="btn btn--ghost btn--sm" disabled={isBusy} onClick={() => askDemoteDeveloper(u)} title="고급: 개발자 권한 해제"><Icon name="lock" size={14} />개발자 해제</button>
                        : <button className="btn btn--ghost btn--sm" disabled={isBusy} onClick={() => askPromoteDeveloper(u)} title="고급: 개발자 권한 부여"><Icon name="lock" size={14} />개발자</button>}
                      <button className="icon-btn icon-btn--danger" disabled={isBusy || isSelf} onClick={() => askDelete(u)} title={isSelf ? '본인 계정은 탈퇴할 수 없습니다' : '탈퇴 처리'}><Icon name="trash" size={15} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {nextToken && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
              <button className="btn btn--ghost" disabled={loadingMore} onClick={loadMore}>
                <Icon name="caret" size={15} />{loadingMore ? '불러오는 중…' : '더 보기'}
              </button>
            </div>
          )}
        </div>
      </div>

      {confirm && (
        <Confirm title={confirm.title} body={confirm.body} confirmLabel={confirm.confirmLabel}
                 onConfirm={confirm.onConfirm} onClose={() => setConfirm(null)} />
      )}
    </div>
  );
}

export default UsersAdmin;
